import { useState, useEffect } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Send, CheckCircle2, HelpCircle, MessageSquare, Bug, Paperclip, X } from "lucide-react";

const CATEGORIES = [
  { value: "fraga", label: "Fråga / Hjälp", icon: HelpCircle },
  { value: "feedback", label: "Feedback", icon: MessageSquare },
  { value: "bugg", label: "Buggrapport", icon: Bug },
];

export function FeedbackForm({ showContactFields = false, defaultCategory = "fraga" }) {
  const [category, setCategory] = useState(defaultCategory);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (!file) { setFilePreview(""); return; }
    const url = URL.createObjectURL(file);
    setFilePreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const okType = (f.type || "").startsWith("image/");
    const okExt = /\.(png|jpe?g|webp|heic|heif)$/i.test(f.name || "");
    if (!okType && !okExt) {
      toast.error("Endast bilder (PNG, JPG, WEBP, HEIC) kan bifogas."); return;
    }
    if (f.size > 15 * 1024 * 1024) { toast.error("Bilden är för stor (max 15 MB)."); return; }
    setFile(f);
  };

  const submit = async () => {
    if (message.trim().length < 3) { toast.error("Skriv gärna ett meddelande."); return; }
    setBusy(true);
    try {
      let attachment_id = null;
      if (file && category === "bugg") {
        const fd = new FormData();
        fd.append("file", file);
        const up = await api.post("/feedback/attachment", fd, { headers: { "Content-Type": "multipart/form-data" } });
        attachment_id = up.data.attachment_id;
      }
      const { data } = await api.post("/feedback", { category, message, name, email, attachment_id });
      toast.success(data.message || "Tack! Vi har tagit emot ditt meddelande.");
      setSent(true);
      setMessage(""); setFile(null);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (sent) {
    return (
      <div className="text-center py-8" data-testid="feedback-success">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary mb-4">
          <CheckCircle2 className="h-7 w-7" />
        </div>
        <h3 className="font-head font-semibold text-lg">Tack för ditt meddelande!</h3>
        <p className="text-sm text-muted-foreground mt-1 max-w-sm mx-auto">Vi läser allt och återkommer så snart vi kan.</p>
        <Button variant="outline" className="rounded-full mt-5" onClick={() => setSent(false)} data-testid="feedback-send-another">
          Skicka ett till
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="feedback-form">
      <div>
        <Label className="mb-1.5 block">Vad gäller det?</Label>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger data-testid="feedback-category" className="h-11"><SelectValue /></SelectTrigger>
          <SelectContent>
            {CATEGORIES.map((c) => (
              <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {showContactFields && (
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label htmlFor="fb-name" className="mb-1.5 block">Ditt namn (valfritt)</Label>
            <Input id="fb-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="För- och efternamn" data-testid="feedback-name" className="h-11" />
          </div>
          <div>
            <Label htmlFor="fb-email" className="mb-1.5 block">Din e-post (valfritt)</Label>
            <Input id="fb-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="namn@exempel.se" data-testid="feedback-email" className="h-11" />
          </div>
        </div>
      )}

      <div>
        <Label htmlFor="fb-message" className="mb-1.5 block">Ditt meddelande</Label>
        <Textarea
          id="fb-message"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Beskriv din fråga, idé eller det du stötte på…"
          rows={6}
          data-testid="feedback-message"
        />
        {showContactFields && (
          <p className="text-xs text-muted-foreground mt-1.5">Lämna din e-post om du vill ha ett svar.</p>
        )}
      </div>

      {category === "bugg" && (
        <div data-testid="feedback-attachment-section">
          <Label className="mb-1.5 block">Bifoga skärmdump (valfritt)</Label>
          {!file ? (
            <label className="flex items-center gap-2 cursor-pointer rounded-xl border border-dashed border-border px-4 py-3 text-sm text-muted-foreground hover:border-primary hover:text-foreground transition-colors min-h-[44px]" data-testid="feedback-file-label">
              <Paperclip className="h-4 w-4" /> Välj en bild (PNG, JPG, WEBP, HEIC – max 15 MB)
              <input type="file" accept="image/*,.png,.jpg,.jpeg,.webp,.heic,.heif" className="sr-only" onChange={onPickFile} data-testid="feedback-file-input" />
            </label>
          ) : (
            <div className="rounded-xl border border-border bg-muted/50 p-3" data-testid="feedback-file-chosen">
              <div className="flex items-center justify-between gap-2 text-sm">
                <span className="flex items-center gap-2 min-w-0"><Paperclip className="h-4 w-4 shrink-0 text-primary" /> <span className="truncate">{file.name}</span></span>
                <button type="button" onClick={() => setFile(null)} className="flex h-11 w-11 items-center justify-center text-muted-foreground hover:text-destructive shrink-0" data-testid="feedback-file-remove"><X className="h-4 w-4" /></button>
              </div>
              {filePreview && (
                <img src={filePreview} alt="Förhandsvisning" onError={() => setFilePreview("")} className="mt-2 max-h-40 rounded-lg border border-border object-contain" data-testid="feedback-file-preview" />
              )}
            </div>
          )}
        </div>
      )}

      <Button className="rounded-full min-h-[44px]" onClick={submit} disabled={busy} data-testid="feedback-submit">
        <Send className="h-4 w-4 mr-1.5" /> {busy ? "Skickar…" : "Skicka meddelande"}
      </Button>
    </div>
  );
}
